import React, { useState } from 'react';
import api from '../services/api';

/**
 * ExpenseModal - Quick expense/egress registration
 * Allows recording cash outflows from the box
 */
const ExpenseModal = ({ sessionId, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        type: 'operational_expense',
        amount: '',
        concept: '',
        beneficiary: '',
        notes: '',
        linked_expense_id: null
    });
    const [evidenceFile, setEvidenceFile] = useState(null);
    const [evidencePreview, setEvidencePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // [AUTOMATION] Pending invoices for supplier payments
    const [pendingInvoices, setPendingInvoices] = useState([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);

    // Fetch pending invoices
    const fetchPendingInvoices = async () => {
        setInvoicesLoading(true);
        try {
            const response = await api.get('/expenses?payment_status=PENDIENTE');
            if (response.data && response.data.success) {
                setPendingInvoices(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching pending invoices:', err);
        } finally {
            setInvoicesLoading(false);
        }
    };

    React.useEffect(() => {
        if (formData.type === 'supplier_payment' && pendingInvoices.length === 0) {
            fetchPendingInvoices();
        }
    }, [formData.type]);

    const handleInvoiceSelect = (invoiceId) => {
        const invoice = pendingInvoices.find(v => v.id === parseInt(invoiceId));
        if (invoice) {
            setFormData(prev => ({
                ...prev,
                linked_expense_id: invoice.id,
                amount: invoice.amount,
                concept: `Pago FC ${invoice.siigo_fc_number || invoice.provider_invoice_number || ''} - ${invoice.provider_name}`,
                beneficiary: invoice.provider_name
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                linked_expense_id: null
            }));
        }
    };

    const expenseTypes = [
        { value: 'supplier_payment', label: 'Pago a Proveedor' },
        { value: 'operational_expense', label: 'Gasto Operativo' },
        { value: 'employee_advance', label: 'Anticipo Empleado' },
        { value: 'bank_deposit', label: 'Consignación Banco' },
        { value: 'withdrawal', label: 'Retiro' },
        { value: 'other', label: 'Otro' }
    ];

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEvidenceFile(file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setEvidencePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                setError('Debe ingresar un monto válido');
                setLoading(false);
                return;
            }

            if (!formData.concept) {
                setError('Debe ingresar un concepto');
                setLoading(false);
                return;
            }

            // Create FormData for file upload
            const submitData = new FormData();
            submitData.append('type', formData.type);
            submitData.append('amount', parseFloat(formData.amount));
            submitData.append('concept', formData.concept);
            submitData.append('beneficiary', formData.beneficiary || '');
            submitData.append('notes', formData.notes || '');
            if (formData.linked_expense_id) {
                submitData.append('linked_expense_id', formData.linked_expense_id);
            }
            if (evidenceFile) {
                submitData.append('evidence', evidenceFile);
            }

            // Use cash-box endpoint instead of cartera
            await api.post('/cash-box/expense', submitData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            console.error('Error registering expense:', err);
            setError(err.response?.data?.error || 'Error al registrar egreso');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-container" style={{ maxWidth: '500px' }}>
                <div className="modal-header">
                    <h2>📤 Registrar Egreso</h2>
                    <button onClick={onClose} className="close-button">×</button>
                </div>

                <div className="modal-body">
                    <form onSubmit={handleSubmit}>
                        {/* Type select */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Tipo de egreso:
                            </label>
                            <select
                                value={formData.type}
                                onChange={(e) => handleChange('type', e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg"
                            >
                                {expenseTypes.map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Invoice selector for supplier payments */}
                        {formData.type === 'supplier_payment' && (
                            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                <label className="block text-sm font-semibold text-orange-800 mb-1">
                                    🔎 Seleccionar Factura Pendiente:
                                </label>
                                <select
                                    value={formData.linked_expense_id || ''}
                                    onChange={(e) => handleInvoiceSelect(e.target.value)}
                                    className="w-full p-2 border border-orange-300 rounded-lg bg-white"
                                    disabled={invoicesLoading}
                                >
                                    <option value="">-- Seleccionar factura (opcional) --</option>
                                    {pendingInvoices.map(inv => (
                                        <option key={inv.id} value={inv.id}>
                                            {inv.siigo_fc_number || inv.provider_invoice_number} - {inv.provider_name} (${parseFloat(inv.amount).toLocaleString()})
                                        </option>
                                    ))}
                                </select>
                                {invoicesLoading && <small className="text-orange-600">Cargando facturas...</small>}
                                {!invoicesLoading && pendingInvoices.length === 0 && (
                                    <small className="text-gray-500">No hay facturas pendientes registradas.</small>
                                )}
                            </div>
                        )}

                        {/* Amount */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Monto: *
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={formData.amount}
                                onChange={(e) => handleChange('amount', e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg"
                                placeholder="$ 0"
                                required
                            />
                        </div>

                        {/* Concept */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Concepto: *
                            </label>
                            <input
                                type="text"
                                value={formData.concept}
                                onChange={(e) => handleChange('concept', e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg"
                                placeholder="Descripción del egreso"
                                required
                            />
                        </div>

                        {/* Beneficiary */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Beneficiario:
                            </label>
                            <input
                                type="text"
                                value={formData.beneficiary}
                                onChange={(e) => handleChange('beneficiary', e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg"
                                placeholder="Nombre del beneficiario (opcional)"
                            />
                        </div>

                        {/* Notes */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Notas:
                            </label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => handleChange('notes', e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg"
                                placeholder="Notas adicionales (opcional)"
                                rows="2"
                            />
                        </div>

                        {/* Evidence Upload */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Evidencia (foto):
                            </label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="w-full p-2 border border-gray-300 rounded-lg"
                            />
                            {evidencePreview && (
                                <div className="mt-2">
                                    <img
                                        src={evidencePreview}
                                        alt="Preview"
                                        className="max-w-full h-32 object-contain border rounded"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Error message */}
                        {error && (
                            <div className="alert-box alert-error mb-4">
                                {error}
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 disabled:bg-gray-400"
                            >
                                {loading ? '⏳ Registrando...' : '✅ Registrar'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ExpenseModal;
